import {
  Calendar,
  CalendarProps,
  InputText,
  InputTextarea,
  CloseIcon,
  useGlobalToast,
  ConfiguredCalendar,
} from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { SymbolPickerToolbar } from '@/ui/components/SymbolPickerToolbar';
import { isEndpointLockError, useCreateChronologyFromSelectionMutation } from '@/api';
import { FC, useMemo, useState, useEffect, useRef, ChangeEvent } from 'react';
import { useDictation } from '@/hooks';
import {
  BROADCAST_ADD_TO_CHRONOLOGY_AND_LIGHT_BUNDLE_REFRESH,
  BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH,
  BROADCAST_FORCE_TIMELINE_REFRESH,
  BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID,
} from '@/broadcast/constants';
import { selectBundlePagesData, useAppDispatch, mergePageInfo, PageInfoData } from '@/store';
import { useFletchersBroadcast } from '@/broadcast';
import {
  useAppSelector,
  selectSharedDataActiveBundleType,
  selectViewportPageIndexInStore,
  selectBundleMetadataByStoreKey,
  makeSelectBundlePageDataByPageIndex,
  selectPageInfoCache,
  selectBundlePageIds,
} from '@/store';
import { format } from 'date-fns';
import { useDraggableResizable } from '../hooks';
import { spliceDictatedText } from './addToChronologyUtils';
import { parseNumbers, validatePagesInput } from '@/ui/pages/TimelineChronologyPage/utils';

interface DialogProps {
  selectedText: string;
  selectedPageIds: string[];
  onClose?: () => void;
  clearSelection?: () => void;
  onSelectedTextForChronology?: (text: string) => void;
}

const AddToChronologyDialog: FC<DialogProps> = ({
  selectedText,
  selectedPageIds,
  onClose,
  clearSelection,
  onSelectedTextForChronology,
}) => {
  const globalToastRef = useGlobalToast();
  const dispatch = useAppDispatch();
  const extractTextRef = useRef<HTMLTextAreaElement>(null);
  const commentTextRef = useRef<HTMLTextAreaElement>(null);
  const isUserTypingRef = useRef<boolean>(false);
  const [comments, setComments] = useState<string>('');
  const [extractText, setExtractText] = useState<string>(selectedText);

  const insertSymbol = (symbol: string) => {
    const textarea = extractTextRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = extractText.slice(0, start) + symbol + extractText.slice(end);
      setExtractText(newText);
      if (onSelectedTextForChronology) {
        onSelectedTextForChronology(newText);
      }
      // Restore cursor position after the inserted symbol
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + symbol.length;
        textarea.focus();
      });
    } else {
      const newText = extractText + symbol;
      setExtractText(newText);
      if (onSelectedTextForChronology) {
        onSelectedTextForChronology(newText);
      }
    }
  };

  // Live dictation splice:
  //   1. `onStart` snapshots the pre-dictation extract text + caret position
  //      into `dictationSnapshotRef`.
  //   2. While `listening` is true, the effect below watches the growing
  //      `dictation.transcript` and splices it into the extract field at
  //      the snapshotted caret — so words appear in place as spoken.
  //   3. `onCancel` restores the snapshot exactly (no half-spoken words
  //      linger). `onCommit` is a no-op — the field already reflects the
  //      final transcript, so we just stop.
  //
  // Snapshot lives in a ref (not state) so mutating it doesn't retrigger
  // the splice effect — only transcript changes should drive re-splices.
  const dictationSnapshotRef = useRef<{ base: string; caret: number } | null>(null);

  const dictation = useDictation({
    onStart: () => {
      const textarea = extractTextRef.current;
      const caret = textarea ? textarea.selectionStart : extractText.length;
      dictationSnapshotRef.current = { base: extractText, caret };
    },
    onCancel: () => {
      const snapshot = dictationSnapshotRef.current;
      if (!snapshot) return;
      setExtractText(snapshot.base);
      onSelectedTextForChronology?.(snapshot.base);
      dictationSnapshotRef.current = null;
    },
    onCommit: () => {
      // Keep whatever's already in the field — the live splice was building
      // it in place. Just clear the snapshot so a next start begins fresh.
      dictationSnapshotRef.current = null;
    },
  });

  useEffect(() => {
    if (!dictation.listening) return;
    const snapshot = dictationSnapshotRef.current;
    if (!snapshot) return;
    const newText = spliceDictatedText(snapshot.base, snapshot.caret, dictation.transcript);
    if (newText !== extractText) {
      setExtractText(newText);
      onSelectedTextForChronology?.(newText);
    }

    // during dictation must NOT retrigger the splice; only transcript should.
  }, [dictation.transcript, dictation.listening]);

  // Same dictation setup for the Comment textarea — independent snapshot ref
  // so its splice doesn't collide with the extract-field dictation.
  const commentDictationSnapshotRef = useRef<{ base: string; caret: number } | null>(null);

  const commentDictation = useDictation({
    onStart: () => {
      const textarea = commentTextRef.current;
      const caret = textarea ? textarea.selectionStart : comments.length;
      commentDictationSnapshotRef.current = { base: comments, caret };
    },
    onCancel: () => {
      const snapshot = commentDictationSnapshotRef.current;
      if (!snapshot) return;
      setComments(snapshot.base);
      commentDictationSnapshotRef.current = null;
    },
    onCommit: () => {
      commentDictationSnapshotRef.current = null;
    },
  });

  useEffect(() => {
    if (!commentDictation.listening) return;
    const snapshot = commentDictationSnapshotRef.current;
    if (!snapshot) return;
    const newText = spliceDictatedText(snapshot.base, snapshot.caret, commentDictation.transcript);
    if (newText !== comments) {
      setComments(newText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentDictation.transcript, commentDictation.listening]);

  const insertSymbolIntoComment = (symbol: string) => {
    const textarea = commentTextRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = comments.slice(0, start) + symbol + comments.slice(end);
      setComments(newText);
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + symbol.length;
        textarea.focus();
      });
    } else {
      setComments(comments + symbol);
    }
  };
  const { sendMessageWithoutResponse } = useFletchersBroadcast();
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const metadataDefault = useAppSelector(selectBundleMetadataByStoreKey());
  const currentPageIndex = useAppSelector(selectViewportPageIndexInStore);
  const currentPageData = useAppSelector(makeSelectBundlePageDataByPageIndex)(currentPageIndex);
  const pageIds = useAppSelector(selectBundlePageIds);
  const currentPageId = pageIds[currentPageIndex];

  // Compact page-number string used to pre-populate the Pages input in
  // the right column ("1, 3-5, 12"). Resolves each selected pageId to
  // its 1-indexed position in the current bundle, sorts, and collapses
  // consecutive numbers into ranges. Uses ASCII hyphens (not en-dashes)
  // so the value round-trips through validatePagesInput / parseNumbers,
  // which both expect `start-end`. Pages the user selected that aren't
  // in the current bundle view (edge case — page removed or a bundle-
  // type mismatch) drop out silently.
  const selectedPagesLabel = useMemo(() => {
    const numbers: number[] = [];
    selectedPageIds.forEach((id) => {
      const idx = pageIds.indexOf(id);
      if (idx >= 0) numbers.push(idx + 1);
    });
    numbers.sort((a, b) => a - b);
    if (numbers.length === 0) return { numberList: '' };
    const segments: string[] = [];
    let runStart = numbers[0];
    let runEnd = numbers[0];
    for (let i = 1; i < numbers.length; i += 1) {
      if (numbers[i] === runEnd + 1) {
        runEnd = numbers[i];
      } else {
        segments.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
        runStart = numbers[i];
        runEnd = numbers[i];
      }
    }
    segments.push(runStart === runEnd ? `${runStart}` : `${runStart}-${runEnd}`);
    return { numberList: segments.join(', ') };
  }, [selectedPageIds, pageIds]);

  const pageInfoCache = useAppSelector(selectPageInfoCache);
  const [createChronologyFromSelection] = useCreateChronologyFromSelectionMutation();
  const bundleData = useAppSelector(selectBundlePagesData);

  const cachedInfo = currentPageId ? pageInfoCache?.[currentPageId] : undefined;
  // Bookmark and cluster dates both come from the lazy pageInfoCache
  // first, then fall back to the initial /bundle response. The cache
  // is where `bundle/page-info` prefetches land (see
  // useThumbnailPageInfoPrefetch); the bundle response is the coarser
  // one-shot payload. Cache-first because it reflects in-session
  // updates that the initial /bundle response wouldn't include.
  const effectiveBookmarkDate = cachedInfo?.bookmarkDate ?? currentPageData?.bookmarkDate;
  const effectiveClusterDate = cachedInfo?.clusterDate ?? currentPageData?.clusterDate;

  const {
    elementRef: dialogRef,
    handleMouseDown,
    handleEdgeResizeMouseDown,
    style: dialogStyle,
  } = useDraggableResizable({
    defaultSize: { width: 700, height: 600 },
    minSize: { width: 700, height: 480 },
    storageKey: 'add-to-chronology-dialog-state',
    initialPosition: 'bottom-left',
  });
  const [disableCreate, setDisableCreate] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<Date | null>(
    effectiveBookmarkDate
      ? new Date(effectiveBookmarkDate)
      : effectiveClusterDate
        ? new Date(effectiveClusterDate)
        : null,
  );

  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  // Pages field — starts populated from the user's current selection
  // ("1, 3-5"). Kept as raw text so the user can edit freely; parsed
  // back to numbers → pageIds at submit time. Also has a small input-
  // focused flag so the red error border only shows on blur, matching
  // the pattern the chronology page's Pages field uses.
  const [pagesInputText, setPagesInputText] = useState<string>('');
  const [isPagesInputFocused, setIsPagesInputFocused] = useState<boolean>(false);
  const isUserEditingPagesRef = useRef<boolean>(false);
  const isPagesInputError = !validatePagesInput(pagesInputText);

  // Track which bundle-page numbers we've already reflected into the
  // Pages field so we can merge in later highlights without wiping the
  // user's manual edits. The alternative — freezing the field once the
  // user has touched it — meant that highlighting more text on other
  // pages silently dropped those pages from the entry.
  const lastSyncedPageNumbersRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Turn the current selectedPageIds into a set of bundle-page numbers
    // (1-indexed). Skip ids that don't resolve into the current bundle
    // view — same policy as `selectedPagesLabel`.
    const currentNumbers = new Set<number>();
    selectedPageIds.forEach((id) => {
      const idx = pageIds.indexOf(id);
      if (idx >= 0) currentNumbers.add(idx + 1);
    });

    const newNumbers: number[] = [];
    currentNumbers.forEach((n) => {
      if (!lastSyncedPageNumbersRef.current.has(n)) newNumbers.push(n);
    });
    lastSyncedPageNumbersRef.current = currentNumbers;

    // Nothing to do if the selection didn't grow. This also handles the
    // "user removed pages from the field but didn't add any new ones"
    // case — we don't want to re-insert numbers they deliberately
    // deleted.
    if (newNumbers.length === 0) return;

    // If the user hasn't touched the field yet, replace it wholesale
    // with the canonical prefill (which collapses consecutive numbers
    // into ranges — nicer to read than the raw comma list).
    if (!isUserEditingPagesRef.current) {
      setPagesInputText(selectedPagesLabel.numberList);
      return;
    }

    // User has edits; append only the numbers they haven't already got
    // in the field. Guards against double-adding when a user manually
    // typed a page they later happen to highlight text on.
    newNumbers.sort((a, b) => a - b);
    setPagesInputText((prev) => {
      const alreadyInField = new Set(parseNumbers(prev));
      const toAppend = newNumbers.filter((n) => !alreadyInField.has(n));
      if (toAppend.length === 0) return prev;
      const suffix = toAppend.join(', ');
      const trimmed = prev.trim();
      if (!trimmed) return suffix;
      const sep = /,\s*$/.test(trimmed) ? ' ' : ', ';
      return `${trimmed}${sep}${suffix}`;
    });
  }, [selectedPageIds, pageIds, selectedPagesLabel.numberList]);

  // Sync extractText with selectedText prop changes (for accumulated text)
  useEffect(() => {
    // Always update the text from selectedText prop
    setExtractText(selectedText);

    // Only scroll if this is not from user typing
    if (!isUserTypingRef.current && selectedText && extractTextRef.current) {
      setTimeout(() => {
        if (extractTextRef.current) {
          extractTextRef.current.scrollTop = extractTextRef.current.scrollHeight;
        }
      }, 0);
    }

    // Reset the flag after processing
    isUserTypingRef.current = false;
  }, [selectedText]);

  // Validate time format (should be valid HH:MM between 00:00 and 23:59)
  const isTimeValid = (time: Date | null): boolean => {
    if (!time) return true; // Time is optional
    const hours = time.getHours();
    const minutes = time.getMinutes();
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  // Resolved list of pageIds the entry will actually be tagged against —
  // parsed from the Pages field and mapped back to pageIds via the
  // bundle's ordered pageIds array. Page(s) is a required field, so an
  // empty or malformed value resolves to `[]` and Create stays disabled
  // (see `isFormValid` below).
  const resolvedPageIds = useMemo<string[]>(() => {
    const trimmed = pagesInputText.trim();
    if (!trimmed || !validatePagesInput(trimmed)) return [];
    const numbers = parseNumbers(trimmed);
    const ids: string[] = [];
    for (const n of numbers) {
      const id = pageIds[n - 1];
      if (id) ids.push(id);
    }
    return ids;
  }, [pagesInputText, pageIds]);

  const isFormValid =
    currentDate !== null &&
    isTimeValid(currentTime) &&
    !isPagesInputError &&
    pagesInputText.trim().length > 0 &&
    resolvedPageIds.length > 0;

  const handleCreateChronologyEntry = () => {
    const { caseId } = metadataDefault;
    if (!caseId || resolvedPageIds.length === 0 || !activeBundleType) {
      console.error('Missing required data for chronology entry');
      return;
    }

    // Only proceed if form is valid
    if (!isFormValid) {
      return;
    }

    const body = {
      caseId,
      pageIds: resolvedPageIds,
      bundleTypeToAddFrom: activeBundleType,
      chronologyEntry: {
        date: format(currentDate!, 'yyyy-MM-dd'),
        time: currentTime ? format(currentTime, 'HH:mm') : null,
        verbatimExtract: extractText,
        comment: comments,
      },
    };

    if (
      body.caseId &&
      body.pageIds &&
      body.pageIds.length > 0 &&
      body.bundleTypeToAddFrom &&
      body.chronologyEntry &&
      body.chronologyEntry.date &&
      body.chronologyEntry.verbatimExtract
    ) {
      //Disable Create button to prevent multiple clicks
      if (!disableCreate) setDisableCreate((disableCreate) => !disableCreate);
      createChronologyFromSelection(body)
        .unwrap()
        .then(({ entryId }) => {
          // Optimistically flag the selected pages as "in chronology" so the
          // thumbnail indicator appears immediately, without waiting for the
          // /bundle refetch. Append to any entry ids already known for the page.
          if (activeBundleType) {
            const chronologyCacheUpdates: Record<string, PageInfoData> = {};
            resolvedPageIds.forEach((pageId) => {
              const existingEntryIds =
                pageInfoCache[pageId]?.chronologyEntryIds ?? bundleData.get(pageId)?.chronologyEntryIds ?? [];
              chronologyCacheUpdates[pageId] = {
                chronologyEntryIds: existingEntryIds.includes(entryId)
                  ? existingEntryIds
                  : [...existingEntryIds, entryId],
              };
            });
            dispatch(mergePageInfo({ key: activeBundleType, data: chronologyCacheUpdates }));
          }

          // Handle timeline refresh — and, if the Chronology tab is open,
          // scroll it to the entry we just created. The scroll broadcast
          // fires just after the refresh so the chronology tab has time
          // to fetch the new entry into its list; the receiving side is
          // safe either way (it queues the pending id and scrolls once
          // the entry appears in currentListEntries).
          //
          // We also fire BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH so
          // the *other* bundle tab (Full ↔ Light) picks up the change:
          // its ReactVirtualizedList listener clears pageInfoCache and
          // refetches the bundle, which updates both the thumbnail
          // "in chronology" flag and our chronology button badge counter.
          // Fires unconditionally — previously this was only dispatched
          // as a side-effect of the "also extracted to light bundle"
          // branch, which meant adding a chronology entry from the Light
          // bundle never notified the Full bundle tab.
          setTimeout(() => {
            sendMessageWithoutResponse(BROADCAST_FORCE_TIMELINE_REFRESH, {
              date: currentDate,
              time: currentTime,
              extract: extractText,
              comment: comments,
            });
            sendMessageWithoutResponse(BROADCAST_SCROLL_TO_CHRONOLOGY_ENTRY_ID, {
              chronologyEntryId: entryId,
            });
            sendMessageWithoutResponse(BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, {});
          }, 850); // Slight delay to ensure backend processing

          // Handle light bundle updates if needed (backend now handles the API call)
          if (activeBundleType === 'full') {
            const pagesToExtractToLB = resolvedPageIds.filter((pageId) => {
              const ele = bundleData.get(pageId);
              const cached = pageInfoCache[pageId];
              const isInLightBundle = cached?.inLightBundle ?? ele?.inLightBundle;
              return !isInLightBundle;
            });

            if (pagesToExtractToLB && pagesToExtractToLB.length > 0) {
              // Update Bundle Data Map - set inLightBundle to true for these pages
              // Update Bundle Data Map - set inLightBundle to true for these pages

              /* OLD BUNDLE ENDPOINT - DISABLED
              const updatedBundleData = new Map(bundleData);
              const cacheUpdates: Record<string, PageInfoData> = {};

              pagesToExtractToLB.forEach((pageId) => {
                const ele = updatedBundleData.get(pageId);
                if (ele) {
                  updatedBundleData.set(pageId, { ...ele, inLightBundle: true });
                }
                cacheUpdates[pageId] = { inLightBundle: true };
              });

              dispatch(mergePageInfo({ key: activeBundleType, data: cacheUpdates }));
              dispatch(
                setBundlePagesData({
                  key: activeBundleType,
                  data: updatedBundleData
                })
              );
              */

              // NEW CACHE LOGIC
              const cacheUpdates: Record<string, PageInfoData> = {};
              pagesToExtractToLB.forEach((pageId) => {
                cacheUpdates[pageId] = { inLightBundle: true };
              });
              dispatch(mergePageInfo({ key: activeBundleType, data: cacheUpdates }));

              // Show success toast
              globalToastRef.current?.show({
                severity: 'success',
                summary: 'Success',
                detail:
                  pagesToExtractToLB.length > 1
                    ? `All ${pagesToExtractToLB.length} selected pages have been successfully added to the Light Bundle`
                    : 'The selected page has been successfully added to the Light Bundle',
                life: 2500,
              });

              // Broadcast refresh
              sendMessageWithoutResponse(BROADCAST_ADD_TO_CHRONOLOGY_AND_LIGHT_BUNDLE_REFRESH, {
                reason: 'Added to chronology successfully',
              });
            }
            globalToastRef.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Chronology entry created successfully',
              life: 2500,
            });
          } else if (activeBundleType === 'light') {
            // Show success toast for chronology only
            globalToastRef.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Chronology entry created successfully',
              life: 2500,
            });
          }

          // Close dialog and clear selection on success
          clearSelection?.();
          onClose?.();
        })
        .catch((error) => {
          console.error('Error creating chronology entry:', error);
          // A lock refusal already has its own toast, and the dialog stays open with the entry
          if (isEndpointLockError(error)) return;
          // Show error toast for chronology failure
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'An error occurred while creating the chronology entry',
            life: 5000,
          });
        })
        .finally(() => {
          setDisableCreate(false);
        });
    }
  };

  return (
    <div
      ref={dialogRef}
      className="absolute z-50 rounded-lg bg-white"
      style={{
        ...dialogStyle,
        border: '1px solid #e1e1e0',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      }}
    >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div
          className="border-fletchers-gray-110 flex cursor-grab items-center justify-between border-b px-[24px] py-[16px] select-none active:cursor-grabbing"
          onMouseDown={handleMouseDown}
        >
          <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Add to Chronology</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="cursor-pointer rounded p-1 focus:outline-none"
          >
            <CloseIcon width={12} height={12} />
          </button>
        </div>

        {/* Body — left: extract, right: date/time/comment/actions */}
        <div className="flex min-h-0 flex-grow">
          {/* Left half — Extract from records */}
          <div className="border-fletchers-gray-110 flex min-h-0 basis-[55%] flex-col border-r px-[24px] pt-[16px] pb-[24px]">
            <label className="mb-2 block text-[14px] font-semibold">Extract from records</label>
            {/* Unified field: the toolbar (Ω, mic) sits directly on top of
                the textarea inside a single bordered container. The wrapper
                owns the border + rounded corners + focus ring; the toolbar
                and textarea inside are both borderless so there's no
                dividing line, and the focus-within selector lifts the whole
                container to the darker focused-state border colour when the
                textarea has focus. */}
            <div className="focus-within:border-fletchers-gray-200 flex min-h-0 flex-grow flex-col overflow-hidden rounded-[4px] border border-gray-300 bg-white transition-colors">
              <SymbolPickerToolbar
                bordered={false}
                onInsertSymbol={insertSymbol}
                dictation={{
                  isSupported: dictation.browserSupportsSpeechRecognition,
                  isListening: dictation.listening,
                  isBusy: dictation.isFinalizing,
                  onStart: dictation.startListening,
                  onCancel: dictation.cancelListening,
                  onConfirm: dictation.confirmListening,
                }}
              />
              <InputTextarea
                ref={extractTextRef}
                value={extractText}
                // Manual edits are locked out while dictation is listening —
                // the base + caret snapshot on which the live splice runs is
                // frozen, so any typing would be overwritten on the next
                // transcript update. Read-only until Done/Cancel.
                readOnly={dictation.listening}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  isUserTypingRef.current = true;
                  setExtractText(event.target.value);
                  if (onSelectedTextForChronology) {
                    onSelectedTextForChronology(event.target.value);
                  }
                }}
                // Wrapper owns the border and focus ring — strip both from
                // the textarea itself so the two elements read as one field.
                className="min-h-0 w-full flex-grow resize-none !rounded-none !border-0 !pt-0 !text-[14px] !shadow-none !ring-0 !outline-none focus:!border-0 focus:!shadow-none focus:!ring-0"
              />
            </div>
          </div>

          {/* Right half — Date+Time on one row, then Pages, then Comment, then Actions */}
          <div className="flex basis-[45%] flex-col px-[24px] pt-[16px] pb-[24px]">
            {/* Date + Time share one row — Time no longer needs a full
                row of its own, which frees vertical space for the new
                Pages field below. */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-2 block text-[14px] font-semibold">Date</label>
                <ConfiguredCalendar
                  showOnFocus
                  value={currentDate}
                  onChange={(event: CalendarProps<'single', Date | (Date | null)[]>) =>
                    setCurrentDate(event.value as Date)
                  }
                  className="w-full rounded-sm bg-white [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
                />
              </div>
              <div>
                <label className="mb-2 block text-[14px] font-semibold">
                  Time <span className="text-fletchers-gray-175 font-light">(optional)</span>
                </label>
                <Calendar
                  value={currentTime}
                  onChange={(event: CalendarProps<'single', Date | (Date | null)[]>) => {
                    const newTime = event.value as Date;
                    if (newTime && isTimeValid(newTime)) {
                      setCurrentTime(newTime);
                    } else if (!newTime) {
                      setCurrentTime(null);
                    }
                  }}
                  onShow={() => {
                    if (!currentTime) {
                      const now = new Date();
                      setCurrentTime(now);
                    }
                  }}
                  // Auto-insert the `:` after two digits so users can type
                  // "1430" instead of "14:30". Fires on every keystroke
                  // that lands in the input; if the raw value is exactly
                  // two digits with no colon we rewrite it in place and
                  // move the caret past the inserted colon. Runs in a
                  // rAF so it happens after PrimeReact's own onInput
                  // handling and doesn't fight it.
                  onInput={(event) => {
                    const input = event.currentTarget as HTMLInputElement;
                    const value = input.value;
                    if (/^\d{2}$/.test(value)) {
                      requestAnimationFrame(() => {
                        input.value = `${value}:`;
                        input.setSelectionRange(3, 3);
                        // Nudge PrimeReact so its internal parse re-runs
                        // against the new value.
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                      });
                    }
                  }}
                  className="w-full rounded-sm bg-white [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
                  timeOnly
                  placeholder="Select time"
                  showSeconds={false}
                />
              </div>
            </div>

            {/* Pages — editable list of bundle page numbers this entry
                is tagged against. Prefilled from the current selection
                (e.g. "1, 3-5"). Same validation shape as the chronology
                page's Pages field: single numbers, ranges with "-", and
                comma-separated combinations. Red border only shows on
                blur so users aren't shouted at while mid-typing. */}
            {/* Wrapper div owns the bottom margin. PrimeReact's InputText
                sometimes drops layout utility classes because the class
                lands on the inner <input> rather than an outer wrapper,
                so putting `mb-4` here guarantees the 16px gap to the
                Comment label below actually renders — matches the gap
                between the Date/Time row and this Pages field above. */}
            <div className="mb-4">
              <label className="mb-2 block text-[14px] font-semibold">Page(s)</label>
              <InputText
                value={pagesInputText}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  isUserEditingPagesRef.current = true;
                  // Accept the raw input verbatim — same behaviour as the
                  // Pages field on the chronology page. `validatePagesInput`
                  // handles rejection of anything malformed (letters,
                  // stray characters, reversed ranges) via the red-border
                  // state on blur; we don't try to filter as they type.
                  setPagesInputText(event.target.value);
                }}
                onFocus={() => setIsPagesInputFocused(true)}
                onBlur={() => setIsPagesInputFocused(false)}
                placeholder="e.g. 1, 3-5"
                inputMode="numeric"
                className={`!h-[32px] w-full !text-[14px] placeholder:!text-[14px] ${
                  !isPagesInputFocused && isPagesInputError ? '!border-fletchers-red-500' : ''
                }`}
              />
            </div>

            <label className="mb-2 block text-[14px] font-semibold">
              Comment <span className="text-fletchers-gray-175 font-light">(optional)</span>
            </label>
            {/* Unified field — same pattern as the Extract-from-records
                field on the left. Wrapper owns border + focus ring;
                toolbar and textarea are borderless inside. Comment now also
                has dictation, driven by its own independent snapshot. */}
            <div className="focus-within:border-fletchers-gray-200 flex min-h-0 flex-grow flex-col overflow-hidden rounded-[4px] border border-gray-300 bg-white transition-colors">
              <SymbolPickerToolbar
                bordered={false}
                onInsertSymbol={insertSymbolIntoComment}
                dictation={{
                  isSupported: commentDictation.browserSupportsSpeechRecognition,
                  isListening: commentDictation.listening,
                  isBusy: commentDictation.isFinalizing,
                  onStart: commentDictation.startListening,
                  onCancel: commentDictation.cancelListening,
                  onConfirm: commentDictation.confirmListening,
                }}
              />
              <InputTextarea
                ref={commentTextRef}
                value={comments}
                readOnly={commentDictation.listening}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setComments(event.target.value)}
                className="min-h-0 w-full flex-grow resize-none !rounded-none !border-0 !pt-0 !text-[14px] !shadow-none !ring-0 !outline-none focus:!border-0 focus:!shadow-none focus:!ring-0"
              />
            </div>

            {/* Action buttons */}
            <div className="mt-auto flex justify-end gap-2 pt-[16px]">
              <OutlineButton className="!h-[32px]" onClick={onClose}>
                Cancel
              </OutlineButton>
              <GreenButton
                className="!h-[32px]"
                label={disableCreate ? 'Creating...' : 'Create'}
                onClick={handleCreateChronologyEntry}
                disabled={!isFormValid || extractText.trim().length === 0 || disableCreate}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edge resize handles */}
      <div
        className="absolute top-0 right-[4px] left-[4px] h-[4px] cursor-n-resize"
        onMouseDown={handleEdgeResizeMouseDown('n')}
      />
      <div
        className="absolute right-[4px] bottom-0 left-[4px] h-[4px] cursor-s-resize"
        onMouseDown={handleEdgeResizeMouseDown('s')}
      />
      <div
        className="absolute top-[4px] bottom-[4px] left-0 w-[4px] cursor-w-resize"
        onMouseDown={handleEdgeResizeMouseDown('w')}
      />
      <div
        className="absolute top-[4px] right-0 bottom-[4px] w-[4px] cursor-e-resize"
        onMouseDown={handleEdgeResizeMouseDown('e')}
      />
      {/* Corner resize handles */}
      <div
        className="absolute top-0 left-0 h-[8px] w-[8px] cursor-nw-resize"
        onMouseDown={handleEdgeResizeMouseDown('nw')}
      />
      <div
        className="absolute top-0 right-0 h-[8px] w-[8px] cursor-ne-resize"
        onMouseDown={handleEdgeResizeMouseDown('ne')}
      />
      <div
        className="absolute bottom-0 left-0 h-[8px] w-[8px] cursor-sw-resize"
        onMouseDown={handleEdgeResizeMouseDown('sw')}
      />
      <div
        className="absolute right-0 bottom-0 h-[8px] w-[8px] cursor-se-resize"
        onMouseDown={handleEdgeResizeMouseDown('se')}
      />
    </div>
  );
};

export default AddToChronologyDialog;
